import {createApp} from "vue3";
import CollectionManager from "../utils/collection-manager";
import FavoritesManager from "../utils/favorites-manager";
import { createPopper } from "../../../../../assets/vendor/@popperjs/core/dist/umd/popper.js";
import CollectionCreationForm from "./collection-creation-form";

export default class CollectionModal{
    constructor($likeButton, $modal, publicationType, publicationId) {
        this.$likeButton = $likeButton;
        this.$modal = $modal;
        // The object id we need to add/remove to/from a collection
        this.publicationId = publicationId;
        // The object type we need to add/remove to/from a collection
        this.publicationType = publicationType;
        this.app = null;
        this.handleFavoriteStateChanged = this.handleFavoriteStateChanged.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);

        // Move the modal directly to the body to escape z-index traps
        document.body.appendChild(this.$modal);

        // Use Popper v2 to handle the positioning
        this.popperInstance = createPopper(this.$likeButton, this.$modal, {
            placement: 'bottom-start',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 5], // Adds a little spacing if needed
                    },
                },{
                    name: 'preventOverflow',
                    options: {
                        boundary: 'viewport', // Prevents it from going off-screen
                        padding: 12, // Keeps it at least 12px away from the edge of the screen
                    },
                },
            ],
        });

        // Escape
        document.addEventListener('keydown', this.handleKeydown);
        // click outside
        document.addEventListener('click', this.documentClicked.bind(this));
        document.addEventListener('favorites:state-changed', this.handleFavoriteStateChanged);

        this.createVueApp();
    }

    /**
     * Create Vue app
     * @return {{loading: boolean, collections: *[]}}
     */
    createVueApp() {
        const publicationId = this.publicationId;
        const publicationType = this.publicationType;

        const app = createApp({
            data() {
                return {
                    publicationId: publicationId,
                    publicationType: publicationType,
                    loading: false,
                    collectionCreationMode: false,
                    collections: [],
                    favoriteRecipes: [],
                    isLiked: false,
                    search: null,
                    overlay: null
                }
            },
            computed: {
                currentCollections() {
                    if (this.search === null || this.search === '') {
                        return this.collections;
                    }

                    return this.collections.filter(collection => collection.title.toLowerCase().includes(this.search.toLowerCase()));
                }
            },
            methods: {
                /**
                 * Set the loading state
                 * @param {boolean} flag
                 */
                setLoading(flag) {
                    this.loading = flag;
                },
                /**
                 * Set visibility for the modal
                 *
                 * @param flag
                 */
                setVisibility(flag) {
                    // When showing the collection modal make sure we are working with the most recent collections
                    if (flag) {
                        this.fetchCollections();
                        this.fetchFavoriteRecipes();
                    }

                    this.$el.parentNode.classList.toggle('actions-modal--visible', flag);
                    this.overlay.classList.toggle('actions-modal-overlay--visible', flag);
                },
                /**
                 * Set collection creation mode
                 * @param flag
                 */
                setCollectionCreationMode(flag) {
                    this.collectionCreationMode = flag;
                },
                /**
                 * Fetch collections
                 * @return {Promise<void>}
                 */
                async fetchCollections() {
                    this.setLoading(true);
                    this.collections = await CollectionManager.getCollections(publicationType);
                    this.setLoading(false);
                },
                /**
                 * Fetch favorite recipes
                 * @return {Promise<void>}
                 */
                async fetchFavoriteRecipes() {
                    if (String(this.publicationType) !== '68') {
                        return;
                    }

                    this.setLoading(true);
                    this.favoriteRecipes = await FavoritesManager.getFavorites(this.publicationType);
                    this.setLoading(false);
                },
                /**
                 * Toggle publication in collection
                 * @param collection
                 */
                togglePublicationInCollection(collection) {
                    if (this.checkCollectionHasPublication(collection)) {
                        this.removePublicationFromCollection(collection.uuid);
                    } else {
                        this.addPublicationToCollection(collection.id);
                    }
                },
                /**
                 * Add publication to collection
                 * @param collectionId
                 */
                async addPublicationToCollection(collectionId) {
                    this.setLoading(true);
                    this.collections = await CollectionManager.addPublicationToCollection(collectionId, this.publicationType, this.publicationId);
                    this.setLoading(false);
                },
                /**
                 * Remove publicationFromCollection
                 * @param collectionId
                 * @return {Promise<void>}
                 */
                async removePublicationFromCollection(collectionId) {
                    this.setLoading(true);
                    this.collections = await CollectionManager.removePublicationFromCollection(collectionId, this.publicationType, this.publicationId);
                    this.setLoading(false);
                },
                /**
                 * Add new collection
                 * @return {Promise<void>}
                 */
                async handleCollectionAdded(newCollection) {
                    this.setLoading(true);
                    // Add the current publication to the newCollection
                    await this.addPublicationToCollection(newCollection.id);

                    // Now we want to sort the collections so we show the new collection first
                    this.collections.sort((collectionA, collectionB) => {
                        if (collectionA.id === newCollection.id) {
                            return -1;
                        }
                        if (collectionB.id === newCollection.id) {
                            return 1;
                        }
                        return 0
                    });

                    this.setLoading(false);
                    this.setCollectionCreationMode(false);
                },
                /**
                 * Toggle the favorite state of the current recipe.
                 * @return {Promise<void>}
                 */
                async toggleFavorite() {
                    if (this.loading || String(this.publicationType) !== '68') {
                        return;
                    }

                    this.setLoading(true);

                    try {
                        const result = await FavoritesManager.togglePublication(this.publicationType, this.publicationId);
                        this.isLiked = result.state;
                    } finally {
                        this.setLoading(false);
                    }
                },
                /**
                 * Get the publicaitons we wan't to use to render the image from
                 *
                 * @param publications
                 * @return {T[]}
                 */
                getPublicationsForImage(publications) {
                    const items = Array.isArray(publications)
                        ? publications
                        : (publications?.publications ?? []);

                    // We just take the first 3 (or less if there aren't 3 publications for this collection);
                    return items.slice(0, 3);
                },
                /**
                 * Check if the given collection already has the current publication
                 * @param collection
                 * @return {*}
                 */
                checkCollectionHasPublication(collection) {
                    return collection.publications.find(publication => publication.hashIdEncrypted === this.publicationId)
                }
            },
            mounted() {
                this.overlay = document.createElement('div');
                this.overlay.classList.add('actions-modal-overlay');

                this.$el.parentNode.after(this.overlay);
            },
            created() {
                this.fetchCollections();
                this.fetchFavoriteRecipes();
            },
        });

        app.component('collection-creation-form', CollectionCreationForm);
        app.config.compilerOptions.delimiters = ['v{', '}'];

        // mount the app
        this.app = app.mount(this.$modal);
    }

    /**
     * Set is liked
     *
     * @param {boolean} state
     */
    setIsLiked(state) {
        this.app.isLiked = state;
    }

    /**
     * Sync favorite state changes initiated elsewhere.
     *
     * @param {{detail: {publicationType: string, publicationId: string, state: boolean, favorites: Array|null}}} event
     */
    handleFavoriteStateChanged(event) {
        const detail = event.detail;
        const isCurrentPublication = String(detail.publicationType) === String(this.publicationType)
            && String(detail.publicationId) === String(this.publicationId);

        if (!isCurrentPublication) {
            return;
        }

        this.setIsLiked(detail.state);

        if (Array.isArray(detail.favorites)) {
            this.app.favoriteRecipes = detail.favorites;
        }
    }

    /**
     * Open modal
     */
    openModal() {
        this.app.setVisibility(true);
    }

    /**
     * Close modal
     */
    closeModal() {
        this.app.setVisibility(false);
    }

    /**
     * Document clicked
     *
     * Click outside logic is handled here
     * @param e
     */
    documentClicked(e) {
        const isClickInside = this.$modal.contains(e.target);
        let isTrigger = false;

        if(this.$likeButton == e.target || this.$likeButton.contains(e.target)){
            isTrigger = true;
        }

        if (!isClickInside && ! isTrigger &&  this.$modal.classList.contains('actions-modal--visible')) {
            this.closeModal(e);
        }
    }

    handleKeydown(e) {
        if (e.key === 'Escape') {
            this.closeModal(e);
        }
    }
}
